import { useState, FormEvent } from 'react';
import { useNavigate, useLocation } from "react-router";
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCustomParam } from '@/hooks';
import { Button } from '@/components/ui/button';
import EmailIcon from '@/components/svg/EmailIcon';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel, FieldSet } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import ArrowleftIcon from '@/components/svg/ArrowleftIcon';
import { useSendEmailVerificationCode } from '@/features/auth/mutations';
import { SparklesIcon } from 'lucide-react';
import { validateEmail } from "@/lib/utils";

const EmailMethodPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const getParam = useCustomParam();
  const username = getParam("username");

  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState('');
  const [email, setEmail] = useState("");
  
  const sendEmailVerificationCode = useSendEmailVerificationCode();

  const handleNavigateBack = () => {
    return navigate(location.pathname + location.search, { 
      state: { view: undefined },
      replace: true 
    });
  };

  const handleNavigateToVerifyCode = () => {
    return navigate(location.pathname + location.search, { 
      state: { view: 'already-has-code' },
      replace: true 
    });
  };

  const handleSignInWithEmail = () => {
    setError("");
    setEmailError('');

    if (!email) {
      setEmailError("Email is required");
      return;
    }

    try {
      validateEmail(email);
    } catch (error) {
      setEmailError((error as Error).message);
      return;
    }

    sendEmailVerificationCode.mutate(
      { email: email, username: username || null },
      {
        onSuccess() {
          setError("");
          navigate(location.pathname + location.search, { 
            state: { view: 'verify-code', email: email },
            replace: true 
          });
        },
        onError(error: unknown) {
          const errorMessage = error instanceof Error ? error.message : "Please contact us at contact@getcollectr.com";
          setError(errorMessage);
        },
      }
    );
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSignInWithEmail();
  };

  return (
    <div className='flex flex-col justify-center my-auto min-h-screen'>
      <Card className='sm:max-w-md w-full mx-auto sm:min-h-min min-h-screen sm:rounded-xl rounded-none'>
        <CardHeader>
          <div className="flex justify-between">
            <div className="mx-auto">
              <img
                className="dark:hidden h-12 text-center block"
                src={"/images/black-logo.png"}
                alt="Collectr"
              />
              <img
                className="hidden h-12 text-center dark:block"
                src={"/images/white-logo.png"}
                alt="Collectr"
              />

            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className='sm:mt-0 mt-4 flex flex-col justify-center'>
            {error && <p className='text-destructive text-sm font-normal mb-4'>{error}</p>}
            
            <FieldSet>
              <form onSubmit={handleSubmit}>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor='email'>Email</FieldLabel>
                    <Input 
                      id='email'
                      required
                      type='email' 
                      className='md:text-sm text-base'
                      placeholder='you@example.com'
                      value={email}
                      aria-invalid={emailError || error ? 'true' : 'false'}
                      onChange={(e) => setEmail(e.target.value.trim())}
                    />
                    {emailError && <FieldError>{emailError}</FieldError>}
                    
                    <FieldDescription onClick={handleNavigateToVerifyCode} className='flex flex-row items-center justify-end text-muted-foreground underline cursor-pointer'>
                      Already have a code?
                    </FieldDescription>
                  </Field>

                  <Field>
                    <Button 
                      onClick={handleSignInWithEmail}
                      className='w-full'
                      type='submit' 
                      variant={'brandPrimary'} 
                      isLoading={sendEmailVerificationCode.isPending}
                      disabled={sendEmailVerificationCode.isPending}
                    >
                      <EmailIcon />
                      Continue with email
                    </Button>
                  </Field>

                  <Alert variant="default" className="[&>svg]:size-4 [&>svg]:stroke-muted-foreground">
                    <SparklesIcon />
                    <AlertDescription>
                      We'll email you a magic code for a password free sign in.
                    </AlertDescription>
                  </Alert>
                </FieldGroup>
              </form>
            </FieldSet>
          </div>

          <Button className='w-full mt-8' variant={'ghost'} onClick={handleNavigateBack}>
            <ArrowleftIcon />
            Back to login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailMethodPage;
